import { Link, useLocation } from '@tanstack/react-router';
import { ArrowLeft, Building2, FileSearch, LayoutDashboard, Receipt, Users } from 'lucide-react';

import logo from '@/assets/OKYC logo.webp';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';

/**
 * Navigation entries for the admin section.
 *
 * Entries are added by the ticket that builds the screen behind them, so this
 * list never points at anything unbuilt.
 *
 * These labels are not the page headings. Each admin screen carries its own
 * `h1`, which the screens originally did without on the grounds that this
 * sidebar already shows which section is open. That was reversed deliberately:
 * the sidebar collapses on mobile, so on a phone the heading was the only
 * thing naming the screen, and a page with no title is a poor landing point
 * from a link or a browser tab. The headings are also worded for the page
 * rather than the nav — "User Management" against "Users" — so the two are
 * expected to differ.
 */
const ADMIN_NAV = [
  { label: 'Overview', to: '/admin', icon: LayoutDashboard },
  { label: 'Users', to: '/admin/users', icon: Users },
  { label: 'Assessments', to: '/admin/assessments', icon: FileSearch },
  { label: 'Companies', to: '/admin/companies', icon: Building2 },
  { label: 'Transactions', to: '/admin/transactions', icon: Receipt },
] as const;

/** The section root, which is Overview's own path rather than a container. */
const ADMIN_ROOT = '/admin';

/**
 * The nav entry a path belongs to, or null for a path under none of them.
 *
 * Exact equality alone would leave the nav unhighlighted on a detail screen —
 * an administrator reading /admin/assessments/<id> would see no section marked
 * open — so an entry also claims the paths beneath it. The section root is
 * excluded from that, because it is Overview's own page and every admin path
 * begins with it: prefix-matching it would mark Overview open everywhere.
 *
 * @param pathname - The current path
 * @returns The `to` of the entry that owns it, or null
 */
export function activeNavEntry(pathname: string): string | null {
  let best: string | null = null;

  for (const item of ADMIN_NAV) {
    const matches =
      pathname === item.to || (item.to !== ADMIN_ROOT && pathname.startsWith(`${item.to}/`));
    if (matches && (best === null || item.to.length > best.length)) {
      best = item.to;
    }
  }

  return best;
}

/**
 * Navigation for the /admin section.
 *
 * Composes the shadcn sidebar primitives rather than wrapping them, so a
 * user-facing sidebar can be a sibling composition over the same parts without
 * a shared abstraction being forced between the two.
 */
export function AdminSidebar() {
  const location = useLocation();
  const active = activeNavEntry(location.pathname);

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" asChild>
              <Link to="/admin">
                {/* The product's own mark, as the auth screens use it, rather
                    than a generic shield. The square box is kept: the sidebar
                    collapses to icon width, and a fixed square is what stays
                    legible once the label beside it is hidden. */}
                <div className="flex aspect-square size-8 items-center justify-center overflow-hidden rounded-lg bg-white">
                  <img src={logo} alt="" className="size-full object-contain p-0.5" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">OKYC</span>
                  <span className="truncate text-xs">Admin</span>
                </div>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {ADMIN_NAV.map((item) => (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton asChild isActive={active === item.to} tooltip={item.label}>
                    <Link to={item.to}>
                      <item.icon />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Back to app">
              <Link to="/">
                <ArrowLeft />
                <span>Back to app</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
